import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { landingPathFor } from '../constants/staffNav';

// AdminEntry — the DoorlyDesk PWA landing page (manifest start_url: /admin).
//
// This route MUST stay at /admin. It does NOT redirect on mount under any
// circumstances — no <Navigate>, no navigate() in an effect. A standalone PWA
// launches at its start_url; an auto-redirect here bounced the URL away from
// /admin (and on iOS can kick the launch out of standalone mode). Instead this
// renders a real page and only navigates when the user taps a button.
//
// DoorlyDesk staff auth is SEPARATE from the borrower app: a successful admin
// login stores `doorly_admin_token` in localStorage (not the borrower
// useAuth() context). We read it via a useState initializer so the correct
// button renders on first paint — sync, no useEffect, no flash.
export default function AdminEntry() {
  const navigate = useNavigate();
  // On desk.godoorly.com routes are prefix-stripped (/dashboard, /login). On
  // app.godoorly.com they keep the /admin prefix. Both resolve on desk, so this
  // just picks the clean URL there.
  const isDesk = typeof window !== 'undefined' && window.location.hostname === 'desk.godoorly.com';
  const [isAdmin] = useState(() => {
    try {
      return !!localStorage.getItem('doorly_admin_token');
    } catch {
      // localStorage blocked (private mode / sandboxed) — treat as logged out.
      return false;
    }
  });
  // ── 🚨 THE BUTTON WENT TO /dashboard FOR EVERYONE ─────────────────────────
  //
  // Jesse: "Instead of landing on the Partners page it tried to land them on the
  // dashboard and it gives them the 'Not a part of your role' page error."
  //
  // 🔑 THIS IS THE PWA'S start_url, so it is the FIRST thing a staff member taps
  // on a phone — and it navigated to a hard-coded /dashboard whatever their role
  // was. Dashboard › Overview is hidden from every role outside
  // BORROWER_DATA_ROLES, so StaffPageGuard refused it and partner_success landed
  // on the explainer instead of on their desk. AdminLogin has resolved its
  // destination with landingPathFor since that guard shipped; this entry point
  // was the one door that never did.
  //
  // ⚠️ THE ROLE IS READ ONCE, SYNCHRONOUSLY, like isAdmin above and for the same
  // reason — the correct destination must be on the button at first paint, not
  // after an effect.
  const [landing] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('doorly_admin_user') || 'null');
      // ⚠️ landingPathFor ALWAYS RETURNS AN /admin-PREFIXED PATH. Both prefixes
      // resolve on desk (App.js mounts the table at '' and at '/admin'), so the
      // prefixed form is correct on either host — and stripping it here would be
      // a second rule to keep true.
      return landingPathFor(u && u.role ? u.role : null);
    } catch {
      return '/admin/login';
    }
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        padding: '24px',
        background: '#ffffff',
        // Fill the iOS status-bar area (black-translucent) with the dark bg.
        paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <img
          src={`${process.env.PUBLIC_URL}/icons/doorly-admin-192.png`}
          alt="DoorlyDesk"
          width={88}
          height={88}
          style={{ display: 'block', margin: '0 auto 18px', borderRadius: 20 }}
        />
        <div style={{ fontSize: 26, fontWeight: 800, color: '#000000', letterSpacing: '-0.01em' }}>
          DoorlyDesk
        </div>
        <div style={{ fontSize: 13, color: '#000000', marginTop: 6 }}>
          Doorly Admin Portal
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate(
          isAdmin
            // 🔑 WHEREVER THIS ROLE'S NAV ACTUALLY STARTS, not a fixed page.
            ? landing
            : (isDesk ? '/login' : '/admin/login')
        )}
        style={{
          width: '100%',
          maxWidth: 320,
          padding: '14px 20px',
          borderRadius: 12,
          border: 'none',
          cursor: 'pointer',
          background: '#3a6ad9',
          color: '#ffffff',
          fontSize: 15,
          fontWeight: 700,
        }}
      >
        {isAdmin ? 'Open DoorlyDesk' : 'Log In'}
      </button>
    </div>
  );
}
